import React, { useState } from 'react'
import { RichText } from '@reward-platform/lift/components'
import { IconSizes } from '@reward-platform/lift/theme/iconSize'
import { Icon } from '@reward-platform/lift/components/Icon'
import { Document } from '@contentful/rich-text-types'
import Hero from '~/components/shared/Hero'
import { useStaticPageContent } from '~/hooks/useStaticPageContent/useStaticPageContent'
import { getHeroWithVariant } from '~/utils/getHeroWithVariant'
import SpinnerOverlay from '../SpinnerOverlay'
import styles from './PolicyPage.module.scss'

interface Policy {
  policyPage: string
  children?: JSX.Element
}

interface AccordionItemProps {
  title: string
  content: Document
}

const AccordionItem = (props: AccordionItemProps) => {
  const { title, content } = props
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <div className={styles.accordionItem}>
      <button
        type="button"
        className={styles.accordionButton}
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
      >
        <h4>{title}</h4>
        <Icon iconName="down-chevron" width={IconSizes.xxs} className={styles.accordionIcon} />
      </button>
      <div className={styles.accordionPanel} aria-expanded={isExpanded}>
        <RichText document={content} openLinksInNewTab />
      </div>
    </div>
  )
}

export default function PolicyPage({ policyPage, children }: Policy): JSX.Element {
  const data = useStaticPageContent(policyPage)

  if (data.isLoading) {
    return <SpinnerOverlay />
  }

  const { hero, content, thumbnail, accordionCollection } = data || {}

  const selectedHero = getHeroWithVariant(hero)
  const heroAlt = selectedHero?.image?.title || selectedHero?.heading || ''

  return (
    <>
      {selectedHero?.image?.file?.url && (
        <Hero src={selectedHero?.image.file.url} alt={heroAlt} sx={styles.hero}>
          <Hero.Headline>{selectedHero?.heading ?? ''}</Hero.Headline>
        </Hero>
      )}
      <div className={styles.container}>
        {thumbnail ? (
          <div className={styles.thumbnail}>
            <p>{thumbnail}</p>
          </div>
        ) : null}
        {content ? <RichText document={content} openLinksInNewTab /> : null}
        {children}
        {accordionCollection?.length ? (
          <div className={styles.accordion}>
            {accordionCollection.map((item) =>
              item.accordionButton && item.accordionPanel ? (
                <AccordionItem
                  key={item.accordionButton}
                  title={item.accordionButton}
                  content={item.accordionPanel}
                />
              ) : null
            )}
          </div>
        ) : null}
      </div>
    </>
  )
}
